"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { sanitizeEmailHtml } from "./sanitize";
import { collapseQuotedTrailer, toggleQuoteRegion } from "../quote-collapse";

/**
 * Shared, security-hardened email-body renderer for WTC apps.
 *
 * Pipeline: `body` → optional app-specific {@link SafeEmailBodyProps.prepareMarkup}
 * (normalization / plaintext→HTML) → {@link sanitizeEmailHtml} (canonical DOMPurify
 * allow-list) → iframe `srcDoc`. Outbound links are intercepted in the parent
 * realm (srcDoc + `allow-same-origin` = same origin; no `allow-scripts`, so the
 * email's own scripts never run) and routed through a confirmation interstitial
 * that shows the true destination before opening it in a new tab with
 * `noopener,noreferrer`. This defeats obfuscated tracking/redirect links common
 * in marketing and phishing mail.
 */

export type SafeEmailBodyVariant = "full" | "snippet";

export interface PrepareMarkupOptions {
  variant: SafeEmailBodyVariant;
  snippetMaxChars: number;
}

export interface SafeEmailBodyProps {
  /** Raw stored/inbound email body (HTML or plaintext). */
  body: string;
  variant?: SafeEmailBodyVariant;
  /** Wrapper class on the outer container. */
  className?: string;
  /** Extra class on the immediate framing wrapper (kept for API stability). */
  proseClassName?: string;
  /** Max chars used when `variant === "snippet"`. */
  snippetMaxChars?: number;
  /**
   * App-specific transform from raw body → UNsanitized display markup, run
   * before {@link sanitizeEmailHtml}. Omit to render the raw body directly
   * (Hub injects its CRM normalization here; the mail client uses the default).
   */
  prepareMarkup?: (body: string, options: PrepareMarkupOptions) => string;
  /**
   * Collapse the quoted-reply / forwarded trailer behind a "show quoted text"
   * toggle (Gmail/Outlook behavior), so a thread rendered as a stack of
   * per-message cards doesn't repeat — and visibly nest — the history inside
   * every later message. Only the DISPLAY collapses; the stored/sent body is
   * untouched. Default `false` (Hub keeps full inline quotes). `"full"` variant
   * only — a `"snippet"` is already a fixed-height preview.
   */
  collapseQuotedReplies?: boolean;
}

interface ParsedLink {
  /** The raw href as authored in the email (opened verbatim on confirm). */
  href: string;
  /** Human-readable host for the destination, or null if it can't be parsed. */
  host: string | null;
}

/**
 * Classify an anchor href. Returns a ParsedLink for http/https links that need
 * a confirmation interstitial, or null for links opened/ignored without one.
 */
function classifyHref(rawHref: string | null): ParsedLink | null {
  if (!rawHref) return null;
  const href = rawHref.trim();
  try {
    const url = new URL(href);
    if (url.protocol === "http:" || url.protocol === "https:") {
      return { href, host: url.host || null };
    }
  } catch {
    return null;
  }
  return null;
}

/** Avoid `</script>` in HTML fragments breaking srcDoc parsing when echoed into a document. */
function escapeClosingScriptTagsForSrcDoc(html: string): string {
  return html.replace(/<\/script/gi, "<\\/script");
}

function buildEmailIframeSrcDoc(fragmentHtml: string): string {
  const bodyHtml = escapeClosingScriptTagsForSrcDoc(fragmentHtml);
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><base target="_blank"><style>
html { color-scheme: light dark; }
html,body{margin:0;padding:10px 12px;font-family:ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-size:14px;line-height:1.6;background:Canvas;color:CanvasText;word-wrap:break-word;overflow-wrap:break-word;}
img,video{max-width:100%;height:auto;}
a{color:LinkText;text-decoration:underline;word-break:break-word;}
pre,code{white-space:pre-wrap;word-break:break-word;}
table{max-width:100%;border-collapse:collapse;}
blockquote{margin:0 0 0 8px;padding-left:10px;border-left:3px solid color-mix(in srgb, CanvasText 25%, transparent);}
[data-quote-toggle]{display:inline-flex;align-items:center;gap:4px;margin:4px 0;padding:1px 8px;font:inherit;font-size:13px;line-height:1.4;font-weight:600;letter-spacing:0.06em;color:color-mix(in srgb, CanvasText 60%, transparent);background:color-mix(in srgb, CanvasText 8%, transparent);border:1px solid color-mix(in srgb, CanvasText 18%, transparent);border-radius:12px;cursor:pointer;}
[data-quote-toggle]:hover{background:color-mix(in srgb, CanvasText 14%, transparent);color:CanvasText;}
[data-quote-content][hidden]{display:none;}
</style></head><body>${bodyHtml}</body></html>`;
}

function EmailContentIframe({
  srcDoc,
  variant,
  collapseQuotedReplies,
  onLinkIntercept,
}: {
  srcDoc: string;
  variant: SafeEmailBodyVariant;
  collapseQuotedReplies: boolean;
  onLinkIntercept: (link: ParsedLink) => void;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Latest interceptor without re-binding the (stable) listener each render.
  const interceptRef = useRef(onLinkIntercept);
  interceptRef.current = onLinkIntercept;

  useLayoutEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let resizeObserver: ResizeObserver | undefined;
    let boundDoc: Document | null = null;

    const resizeToContent = (): void => {
      if (variant !== "full") return;
      try {
        const doc = iframe.contentDocument;
        if (!doc?.documentElement) return;
        const bodyH = doc.body?.scrollHeight ?? 0;
        const docH = doc.documentElement.scrollHeight;
        iframe.style.height = `${Math.ceil(Math.max(bodyH, docH, 24))}px`;
      } catch {
        /* srcDoc not ready or access restricted — leave default height */
      }
    };

    // Toggle the collapsed quoted trailer. Returns true when it handled the
    // click (so the link path is skipped).
    const handleQuoteToggle = (target: Element | null): boolean => {
      if (!target?.closest?.("[data-quote-toggle]")) return false;
      const doc = iframe.contentDocument;
      if (doc && toggleQuoteRegion(doc) !== null) {
        resizeToContent();
      }
      return true;
    };

    // Capture-phase handler bound on the iframe document. Runs in the parent
    // realm (allowed via srcDoc + allow-same-origin) and fires regardless of
    // allow-scripts, which only gates the frame's OWN scripts. Swallows native
    // navigation and routes through the confirm interstitial.
    const handleClick = (event: Event): void => {
      const target = event.target as Element | null;
      if (handleQuoteToggle(target)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const anchor = target?.closest?.("a");
      if (!anchor) return;
      const rawHref = anchor.getAttribute("href");
      event.preventDefault();
      event.stopPropagation();
      const link = classifyHref(rawHref);
      if (link) {
        interceptRef.current(link);
        return;
      }
      // mailto:/tel: are safe protocol handlers — open without an interstitial.
      // Everything else (relative, fragments, javascript:) is a no-op.
      const trimmed = rawHref?.trim() ?? "";
      if (/^(mailto|tel):/i.test(trimmed)) {
        window.open(trimmed, "_blank", "noopener,noreferrer");
      }
    };

    const onLoad = (): void => {
      try {
        const doc = iframe.contentDocument;
        if (!doc?.body) {
          resizeToContent();
          return;
        }
        doc.addEventListener("click", handleClick, true);
        boundDoc = doc;
        // Collapse before the first measure so the initial height is the
        // collapsed height (no expand-then-shrink flash).
        if (collapseQuotedReplies && variant === "full") {
          collapseQuotedTrailer(doc);
        }
        resizeToContent();
        if (variant === "full" && !resizeObserver && typeof ResizeObserver !== "undefined") {
          resizeObserver = new ResizeObserver(resizeToContent);
          resizeObserver.observe(doc.body);
        }
      } catch {
        /* ignore */
      }
    };

    iframe.addEventListener("load", onLoad);
    queueMicrotask(onLoad);

    return () => {
      iframe.removeEventListener("load", onLoad);
      resizeObserver?.disconnect();
      boundDoc?.removeEventListener("click", handleClick, true);
    };
  }, [srcDoc, variant, collapseQuotedReplies]);

  return (
    <iframe
      ref={iframeRef}
      title="Email body"
      srcDoc={srcDoc}
      sandbox="allow-same-origin"
      className={variant === "snippet" ? "block w-full bg-transparent h-[4.85rem] overflow-hidden" : "block w-full bg-transparent min-h-[1.5rem]"}
      style={{ border: "none", width: "100%" }}
    />
  );
}

export function SafeEmailBody({
  body,
  variant = "full",
  className,
  proseClassName,
  snippetMaxChars = 300,
  prepareMarkup,
  collapseQuotedReplies = false,
}: SafeEmailBodyProps) {
  const [pendingLink, setPendingLink] = useState<ParsedLink | null>(null);

  const srcDoc = useMemo(() => {
    if (!body) return "";
    const prepared = prepareMarkup
      ? prepareMarkup(body, { variant, snippetMaxChars })
      : body;
    const sanitized = sanitizeEmailHtml(prepared);
    return sanitized ? buildEmailIframeSrcDoc(sanitized) : "";
  }, [body, variant, snippetMaxChars, prepareMarkup]);

  const onLinkIntercept = useCallback((link: ParsedLink) => {
    setPendingLink(link);
  }, []);

  const confirmNavigation = useCallback(() => {
    setPendingLink((current) => {
      if (current) {
        window.open(current.href, "_blank", "noopener,noreferrer");
      }
      return null;
    });
  }, []);

  if (!srcDoc) {
    return null;
  }

  // Appearance-neutral: the caller owns the wrappers (`className` = outer,
  // `proseClassName` = the div framing the iframe). This lets Hub apply its
  // timeline border / snippet gradient-mask and the mail client its own border,
  // each in its own design tokens, while the iframe + sanitize + interstitial
  // stay shared.
  return (
    <div className={className}>
      <div className={proseClassName}>
        <EmailContentIframe
          srcDoc={srcDoc}
          variant={variant}
          collapseQuotedReplies={collapseQuotedReplies}
          onLinkIntercept={onLinkIntercept}
        />
      </div>
      <LinkConfirmDialog
        link={pendingLink}
        onCancel={() => setPendingLink(null)}
        onConfirm={confirmNavigation}
      />
    </div>
  );
}

/**
 * Interstitial shown before following any link inside an email. Surfaces the
 * true destination host + full URL so the user can spot a mismatch (e.g. a
 * tracking/redirect domain) before leaving. Self-contained (portal + inline
 * styles + system colors) so it renders identically in every app with no
 * dependency on the host design system.
 */
function LinkConfirmDialog({
  link,
  onCancel,
  onConfirm,
}: {
  link: ParsedLink | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  // Escape closes the dialog.
  useEffect(() => {
    if (!link) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [link, onCancel]);

  if (!link || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Leaving your inbox"
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483647, // z-index-allow: link-confirm security interstitial portalled to body — must render above sandboxed email iframe content and any host-app modal; shared pkg has no host Tailwind z-scale
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        background: "rgba(0,0,0,0.5)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          colorScheme: "light dark",
          width: "100%",
          maxWidth: "440px",
          background: "Canvas",
          color: "CanvasText",
          border: "1px solid color-mix(in srgb, CanvasText 20%, transparent)",
          borderRadius: "12px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
          padding: "20px",
          fontFamily:
            'ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
          <span aria-hidden="true" style={{ fontSize: "18px" }}>
            ⚠️
          </span>
          <h2 style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>Leaving your inbox</h2>
        </div>
        <p style={{ margin: "0 0 14px", fontSize: "13px", lineHeight: 1.5, opacity: 0.85 }}>
          This link goes to an external site. Check the address before continuing — WTC
          hasn&rsquo;t verified where it leads.
        </p>

        {link.host && (
          <div style={{ marginBottom: "10px" }}>
            <div style={labelStyle}>Destination</div>
            <div style={{ fontSize: "14px", fontWeight: 600, wordBreak: "break-all" }}>
              {link.host}
            </div>
          </div>
        )}
        <div style={{ marginBottom: "18px" }}>
          <div style={labelStyle}>Full link</div>
          <div
            style={{
              maxHeight: "8rem",
              overflowY: "auto",
              background: "color-mix(in srgb, CanvasText 8%, transparent)",
              borderRadius: "6px",
              padding: "8px",
              fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace",
              fontSize: "12px",
              wordBreak: "break-all",
            }}
          >
            {link.href}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
          <button type="button" onClick={onCancel} style={secondaryButtonStyle}>
            Cancel
          </button>
          <button type="button" onClick={onConfirm} style={primaryButtonStyle}>
            Continue to site ↗
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: "11px",
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  opacity: 0.6,
  marginBottom: "2px",
};

const baseButtonStyle: React.CSSProperties = {
  fontSize: "13px",
  fontWeight: 500,
  padding: "8px 14px",
  borderRadius: "8px",
  cursor: "pointer",
};

const secondaryButtonStyle: React.CSSProperties = {
  ...baseButtonStyle,
  background: "transparent",
  color: "CanvasText",
  border: "1px solid color-mix(in srgb, CanvasText 30%, transparent)",
};

const primaryButtonStyle: React.CSSProperties = {
  ...baseButtonStyle,
  background: "#245397",
  color: "#ffffff",
  border: "1px solid #245397",
};
